import type { MetaData } from '../../../../amplienceTypes/schemas/imported/generic-meta-data-schema';
import type { ReactElement } from 'react';

import { useRouter } from 'next/compat/router';
import Head from 'next/head';

import { PageTypes } from '../../../../utilities/constants';
import { COUNTRY_NAME, SEARCH_TERM } from '../../../../utilities/constants/amplienceVariables';
import { useAppContext } from '../../../../utilities/context/static/AppContext';
import { useProductListContext } from '../../../../utilities/context/static/ProductListContext';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { checkForFacetKeysInPath } from '../../../../utilities/helpers';
import { getAndroidPlayPackageId } from '~/utilities/constants/androidAppBanner';

interface ProductListMetaHeadInterface {
  pageType: PageTypes;
  metaData?: MetaData;
}

export const ProductListStaticMetaHead = ({
  pageType,
  metaData,
}: ProductListMetaHeadInterface): ReactElement => {
  const {
    configuration: { siteNameDisplay, appStoreId, appStoreName, enableUniversalLinks },
  } = useStaticContext();
  const { country, siteType } = useAppContext();
  const androidPackageId = getAndroidPlayPackageId(siteType);
  const router = useRouter();
  const query = router?.query ?? {};
  const asPath = router?.asPath ?? '';
  const { categoryDetails = {}, pageContent, isPLP } = useProductListContext();
  const searchQuery =
    (Array.isArray(query.q) ? query.q.at(0) : query.q) ||
    decodeURIComponent(asPath.match(/[&?]q=(?<searchTerm>.*)(&|$)/)?.groups?.searchTerm || '') ||
    '';

  const encodedSearchQuery = encodeURIComponent(searchQuery);
  const { categoryCode } = categoryDetails || {};
  const metaSiteName = siteNameDisplay || '';
  const searchAppUrl = `${appStoreName}://search/${encodedSearchQuery}`;
  const categoryAppUrl = `${appStoreName}://categories/${categoryCode}`;

  let metaTitle = `${metaData?.pageTitle} ${country.toUpperCase()}`;
  let metaDescription = metaData?.pageDescription
    ?.replace(SEARCH_TERM, searchQuery)
    ?.replace(COUNTRY_NAME, country.toUpperCase());
  let metaRobotsContent = 'noindex, follow';

  if (pageType === PageTypes.CATEGORY) {
    metaTitle = (
      pageContent?.pageMeta?.title ||
      categoryDetails?.pageTitle ||
      metaData?.pageTitle ||
      ''
    ).concat(` ${country.toUpperCase()}`);
    metaDescription =
      pageContent?.pageMeta?.description ||
      categoryDetails?.metaDescription ||
      metaData?.pageDescription;
    metaRobotsContent = 'index, follow';

    const SORT_FILTER_KEY = 'sort';

    const facetKeys = [
      ...(categoryDetails.visibleFacetsV2?.map(item => item?.code) || []),
      SORT_FILTER_KEY,
    ];

    if (facetKeys && checkForFacetKeysInPath(facetKeys as string[], asPath)) {
      metaRobotsContent = 'noindex, follow';
    }
  }

  return (
    <Head>
      <title key="meta-title">{metaTitle}</title>

      <meta key="meta-description" name="description" content={metaDescription} />
      <meta key="meta-robots" name="robots" content={metaRobotsContent} />

      <meta key="meta-og:title" property="og:title" content={metaTitle} />
      <meta key="meta-og:description" property="og:description" content={metaDescription} />
      <meta key="meta-og:site_name" property="og:site_name" content={metaSiteName} />

      {/**
       * There are issues rendering meta tags in head probably due to race condition
       * between hydration and 3rd party scripts injecting code in head
       *
       * Don't try to optize the conditions bellow to look more appealing to your eye
       * by following DRY and nesting conditions one inside another
       *
       * You might risk breaking the tags!
       */}
      {enableUniversalLinks ? (
        <>
          {appStoreId && (
            <meta
              key={`meta-al:ios:app_store_id-${appStoreId}`}
              property="al:ios:app_store_id"
              content={appStoreId}
            />
          )}

          {appStoreName && (
            <meta
              key={`meta-al:ios:app_name-${appStoreName}`}
              property="al:ios:app_name"
              content={appStoreName}
            />
          )}

          {appStoreName && !isPLP && encodedSearchQuery && (
            <meta
              key={`meta-al:ios:url-${encodedSearchQuery}`}
              property="al:ios:url"
              content={searchAppUrl}
            />
          )}

          {appStoreName && isPLP && categoryCode && (
            <meta
              key={`meta-al:ios:url-${categoryCode}`}
              property="al:ios:url"
              content={categoryAppUrl}
            />
          )}

          {androidPackageId && (
            <meta
              key={`meta-al:android:package-${androidPackageId}`}
              property="al:android:package"
              content={androidPackageId}
            />
          )}

          {androidPackageId && appStoreName && !isPLP && encodedSearchQuery && (
            <meta
              key={`meta-al:android:url-${encodedSearchQuery}`}
              property="al:android:url"
              content={searchAppUrl}
            />
          )}

          {androidPackageId && appStoreName && isPLP && categoryCode && (
            <meta
              key={`meta-al:android:url-${categoryCode}`}
              property="al:android:url"
              content={categoryAppUrl}
            />
          )}
        </>
      ) : null}
    </Head>
  );
};
